import React, { useState, useEffect } from "react";
import {
  Card,
  Input,
  Button,
  Alert,
  Space,
  Typography,
  List,
  Tag,
  Divider,
  Row,
  Col,
  message,
  Spin,
  DatePicker,
  Modal,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowRightOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  SearchOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useVoucher } from "./VoucherContext";
import { useTable } from "../Table/TableContext";
import moment from "moment";
import Navbar from "../../../layouts/navbar";
import Footer from "../../../layouts/footer";
import { observer } from "mobx-react-lite";

const { Text, Title } = Typography;

const VoucherTableGuestBooking = observer((props: any) => {
  const { fetchUserVoucherDetail } = useVoucher();
  const { table, fetchTable, onCurrentBooking } = useTable();
  const navigate = useNavigate();

  // Voucher Management States
  const [voucherCodeInput, setVoucherCodeInput] = useState("");
  const [verifyingVoucher, setVerifyingVoucher] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const [workingVouchers, setWorkingVouchers] = useState<any[]>([]);
  const [savedVouchers, setSavedVouchers] = useState<any[]>([]);
  const [isEditMode, setIsEditMode] = useState(true);

  // Table Search States
  const [selectedRestaurant, setSelectedRestaurant] = useState<any>(null);
  const [adultPax, setAdultPax] = useState(0);
  const [childPax, setChildPax] = useState(0);
  const [bookingDate, setBookingDate] = useState("");
  const [searchingTables, setSearchingTables] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Initialize from any passed state (if coming from modal)
  useEffect(() => {
    // You can add logic here to initialize from navigation state if needed
  }, []);

  // Handle voucher verification
  const handleVerifyVoucher = async () => {
    if (!voucherCodeInput.trim()) {
      setVerificationError("Please enter a voucher code");
      return;
    }

    // Check if voucher already exists in the list
    if (
      workingVouchers.some((v) => v.voucher_code === voucherCodeInput.trim())
    ) {
      setVerificationError("This voucher has already been added");
      return;
    }

    setVerifyingVoucher(true);
    setVerificationError("");

    try {
      const response = await fetchUserVoucherDetail(voucherCodeInput.trim());

      // Handle array response
      const voucherData = Array.isArray(response) ? response[0] : response;

      if (!voucherData) {
        setVerificationError("Voucher not found");
        return;
      }

      // Check if voucher is available (status not "used" and usedate is null)
      if (voucherData.status === "used" || voucherData.usedate) {
        setVerificationError("This voucher has already been used");
        return;
      }

      if (
        voucherData.status === "expired" ||
        moment().isAfter(voucherData.expired_date)
      ) {
        setVerificationError("This voucher has expired");
        return;
      }

      // Add to working vouchers list with proper fields from API
      const newVoucher = {
        ...voucherData,
        voucher_code: voucherCodeInput.trim(),
        voucher_type: voucherData.voucher_title, // Using voucher_title as type identifier
        verified: true,
      };

      setWorkingVouchers((prev) => [...prev, newVoucher]);
      setVoucherCodeInput(""); // Clear input after successful verification
    } catch (error) {
      setVerificationError("Invalid voucher code or voucher not found");
    } finally {
      setVerifyingVoucher(false);
    }
  };

  // Remove voucher from working list
  const handleRemoveVoucher = (index: number) => {
    setWorkingVouchers((prev) => prev.filter((_, i) => i !== index));
  };

  // Save vouchers and exit edit mode
  const handleSaveVouchers = () => {
    if (workingVouchers.length === 0) {
      setVerificationError("Please add at least one voucher");
      return;
    }

    // Check if all vouchers have the same voucher_title (type)
    const firstVoucherType = workingVouchers[0].voucher_title;
    const allSameType = workingVouchers.every(
      (voucher) => voucher.voucher_title === firstVoucherType
    );

    if (!allSameType) {
      const differentTypes = [
        ...new Set(workingVouchers.map((v) => v.voucher_title)),
      ];
      setVerificationError(
        `All vouchers must be of the same type. You have different types: ${differentTypes.join(
          ", "
        )}. Please ensure all vouchers are for the same dining experience.`
      );
      return;
    }

    // Check if all vouchers are from the same store
    const firstStore = workingVouchers[0].store_name;
    const allSameStore = workingVouchers.every(
      (voucher) => voucher.store_name === firstStore
    );

    if (!allSameStore) {
      setVerificationError(
        "All vouchers must be from the same restaurant. Please ensure all vouchers are valid for the same location."
      );
      return;
    }

    // Save vouchers and update derived data
    setSavedVouchers([...workingVouchers]);
    setSelectedRestaurant({
      store_id: workingVouchers[0].store_id,
      name: workingVouchers[0].store_name,
    });
    updatePaxFromVouchers(workingVouchers);
    setIsEditMode(false);
    setVerificationError("");

    // Reset table search since vouchers changed
    setHasSearched(false);

    message.success(
      `Saved ${workingVouchers.length} vouchers for table booking`
    );
  };

  // Enter edit mode
  const handleEditVouchers = () => {
    setIsEditMode(true);
    setHasSearched(false); // Reset table search when editing
    message.info("You can now modify your voucher selection");
  };

  // Update pax counts based on vouchers
  const updatePaxFromVouchers = (vouchers: any[]) => {
    let adults = 0;
    let children = 0;

    vouchers.forEach((voucher) => {
      if (voucher.price_type?.toLowerCase().includes("adult")) {
        adults += 1;
      } else if (voucher.price_type?.toLowerCase().includes("child")) {
        children += 1;
      }
      // Default to adult if no clear type
      else {
        adults += 1;
      }
    });

    setAdultPax(adults);
    setChildPax(children);
  };

  // Handle table search
  const handleSearchTables = () => {
    if (!bookingDate) {
      message.error("Please select a booking date");
      return;
    }

    if (savedVouchers.length === 0) {
      message.error("No vouchers saved for booking");
      return;
    }

    setSearchingTables(true);
    setHasSearched(true);

    // Prepare voucher codes for API
    const voucherCodes: any = savedVouchers
      .map((v) => v.voucher_code)
      .join(",");

    // console.log("check vuchercodes ===> ", savedVouchers);

    fetchTable({
      // @ts-ignore
      store_id: selectedRestaurant?.store_id,
      date: bookingDate,
      adultPax: adultPax,
      childPax: childPax,
      voucher_code: savedVouchers[0].orderid,
    })
      .then((response: any) => {
        setSearchingTables(false);
        if (response.timeSlots && response.timeSlots.length > 0) {
          message.success(
            `Found ${response.timeSlots.length} available time slots!`
          );
        } else {
          message.info(
            "No available time slots found for the selected criteria"
          );
        }
      })
      .catch((error: any) => {
        setSearchingTables(false);
        message.error("Failed to search for tables. Please try again.");
        console.error("Table search error:", error);
      });
  };

  // Handle table selection
  const handleSelectTable = (timeSlot: any) => {
    if (savedVouchers.length === 0) {
      message.error("No vouchers selected for booking");
      return;
    }

    Modal.confirm({
      title: "Confirm Table Booking",
      content: (
        <Space direction="vertical">
          <Text>Are you sure you want to book:</Text>
          <Text strong>{selectedRestaurant?.name}</Text>
          <Text>
            Date:{" "}
            <Text strong>{moment(bookingDate).format("DD MMM YYYY")}</Text>
          </Text>
          <Text>
            Time: <Text strong>{timeSlot.time_slot}</Text>
          </Text>
          <Text>
            Party: <Text strong>{adultPax} Adults</Text>
            {childPax > 0 && (
              <>
                , <Text strong>{childPax} Children</Text>
              </>
            )}
          </Text>
          <Text>
            Vouchers: <Text strong>{savedVouchers.length} vouchers</Text>
          </Text>
        </Space>
      ),
      okText: "Yes, Proceed to Checkout",
      cancelText: "Cancel",
      onOk() {
        // Prepare booking data
        const bookingData = {
          selectedProduct: table,
          bookingParams: {
            store_id: selectedRestaurant?.store_id,
            store_name: selectedRestaurant?.name,
            datereserve: bookingDate,
            timereserve: timeSlot.time_slot,
            number_adult: adultPax,
            number_child: childPax,
            name: "", // Guest user - will fill in checkout
            phone: "",
            email: "",
            selected_vouchers: savedVouchers,
            voucher_ids: savedVouchers.map((v) => v.voucher_code),
            deposit_amount: timeSlot.Deposit || 0,
          },
        };

        console.log("Guest voucher booking data:", bookingData);
        onCurrentBooking(bookingData);

        // Navigate to guest checkout page
        navigate("/voucher/guest-checkout", {
          state: {
            verifiedVouchers: savedVouchers,
            bookingData: bookingData,
            restaurant: selectedRestaurant,
          },
        });
      },
    });
  };

  // Calculate if all selectable vouchers are selected
  const areAllSelected = workingVouchers.length > 0;

  return (
    <div id="HomeScreen">
      <div className="container">
        <Navbar hideBanner={true} />

        <div style={{}}>
          <Title level={3}>Book Table with Vouchers</Title>
          <Text type="secondary">
            Add your vouchers and search for available tables
          </Text>

          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            {/* Left Column - Voucher Management */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space>
                    <TableOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                    Assign Voucher
                    {!isEditMode && <Tag color="green">Saved</Tag>}
                  </Space>
                }
                extra={
                  isEditMode ? (
                    <Button
                      type="primary"
                      icon={
                        <SaveOutlined
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                      }
                      onClick={handleSaveVouchers}
                      disabled={workingVouchers.length === 0}
                    >
                      Save
                    </Button>
                  ) : (
                    <Button
                      icon={
                        <EditOutlined
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                      }
                      onClick={handleEditVouchers}
                    >
                      Edit
                    </Button>
                  )
                }
              >
                {isEditMode ? (
                  // EDIT MODE - User can add/remove vouchers
                  <>
                    <div style={{ marginBottom: 16 }}>
                      {/* <Text strong>Add Voucher Code</Text> */}
                      <div
                        style={{
                          width: "100%",
                          marginTop: 8,
                          display: "flex",
                          gap: 10,
                        }}
                      >
                        <Input
                          placeholder="Enter voucher code"
                          value={voucherCodeInput}
                          onChange={(e) => {
                            setVoucherCodeInput(e.target.value.toUpperCase());
                            setVerificationError("");
                          }}
                          onPressEnter={handleVerifyVoucher}
                          disabled={verifyingVoucher}
                          size="large"
                        />
                        <Button
                          type="primary"
                          onClick={handleVerifyVoucher}
                          loading={verifyingVoucher}
                          size="large"
                        >
                          {verifyingVoucher ? "Verifying..." : "Add"}
                        </Button>
                      </div>

                      {verificationError && (
                        <Alert
                          message={verificationError}
                          type="error"
                          showIcon
                          style={{ marginTop: 8 }}
                          closable
                          onClose={() => setVerificationError("")}
                        />
                      )}
                    </div>

                    {workingVouchers.length > 0 && (
                      <>
                        <Divider />
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 12,
                          }}
                        >
                          <Text strong>
                            Verified Vouchers ({workingVouchers.length})
                          </Text>
                          <Button
                            type="link"
                            danger
                            size="small"
                            onClick={() => setWorkingVouchers([])}
                            style={{ padding: 0, height: "auto" }}
                          >
                            Clear All
                          </Button>
                        </div>
                        <List
                          dataSource={workingVouchers}
                          renderItem={(voucher, index) => (
                            <List.Item
                              actions={[
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  onClick={() => handleRemoveVoucher(index)}
                                  style={{
                                    // padding: "4px 8px",
                                    height: "auto",
                                    // fontSize: "12px",
                                  }}
                                >
                                  <CloseCircleOutlined
                                    onPointerEnterCapture={undefined}
                                    onPointerLeaveCapture={undefined}
                                  />
                                </Button>,
                              ]}
                              style={{
                                padding: "12px 0",
                                borderBottom: "1px solid #f0f0f0",
                              }}
                            >
                              <List.Item.Meta
                                avatar={
                                  <div
                                    style={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      backgroundColor: "#52c41a",
                                      marginTop: 10,
                                    }}
                                  />
                                }
                                title={
                                  <Text
                                    strong
                                    style={{
                                      fontSize: "14px",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {voucher.voucher_title}
                                  </Text>
                                }
                                description={
                                  <div style={{ marginTop: 4 }}>
                                    {/* First row: Code and Type */}
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        marginBottom: 4,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <Text
                                        type="secondary"
                                        style={{
                                          fontSize: "12px",
                                          minWidth: 40,
                                        }}
                                      >
                                        Code:
                                      </Text>
                                      <Text
                                        code
                                        style={{
                                          // fontSize: "11px",
                                          // backgroundColor: "#f5f5f5",
                                          // padding: "2px 8px",
                                          borderRadius: 4,
                                          fontFamily: "monospace",
                                          // maxWidth: "140px",
                                          overflow: "hidden",
                                        }}
                                        title={voucher.voucher_code} // Show full code on hover
                                      >
                                        {voucher.voucher_code.length > 16
                                          ? `${voucher.voucher_code.substring(
                                              0,
                                              8
                                            )}...${voucher.voucher_code.substring(
                                              voucher.voucher_code.length - 8
                                            )}`
                                          : voucher.voucher_code}
                                      </Text>
                                      <Tag
                                        color="blue"
                                        style={{
                                          margin: 0,
                                          fontSize: "11px",
                                          padding: "0 6px",
                                          lineHeight: "18px",
                                        }}
                                      >
                                        {voucher.price_type}
                                      </Tag>
                                    </div>

                                    {/* Second row: Restaurant and Expiry */}
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <Text
                                        type="secondary"
                                        style={{
                                          fontSize: "12px",
                                          minWidth: 40,
                                        }}
                                      >
                                        Store:
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: "12px",
                                          fontWeight: 500,
                                        }}
                                      >
                                        {voucher.store_name}
                                      </Text>
                                    </div>

                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        marginTop: 2,
                                      }}
                                    >
                                      <Text
                                        type="secondary"
                                        style={{
                                          fontSize: "12px",
                                          minWidth: 40,
                                        }}
                                      >
                                        Expires:
                                      </Text>
                                      <Text style={{ fontSize: "12px" }}>
                                        {moment(voucher.expired_date).format(
                                          "DD MMM YYYY"
                                        )}
                                      </Text>
                                    </div>
                                  </div>
                                }
                              />
                            </List.Item>
                          )}
                          style={{ marginTop: 8 }}
                        />
                      </>
                    )}

                    {workingVouchers.length > 1 && (
                      <Alert
                        message={`Voucher Type: ${workingVouchers[0].voucher_title}`}
                        description="All vouchers must be of the same type for booking"
                        type="info"
                        showIcon
                        style={{ marginTop: 12 }}
                      />
                    )}
                  </>
                ) : (
                  // SAVED MODE - Vouchers are locked
                  <>
                    <Alert
                      message="Vouchers Saved Successfully"
                      // description={`${savedVouchers.length} vouchers are ready for table booking`}
                      type="success"
                      showIcon
                    />

                    <List
                      dataSource={savedVouchers}
                      renderItem={(voucher, index) => (
                        <List.Item
                          style={{
                            padding: "12px 0",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <List.Item.Meta
                            avatar={
                              <div
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  backgroundColor: "#52c41a",
                                  marginTop: 10,
                                }}
                              />
                            }
                            title={
                              <Text
                                strong
                                style={{ fontSize: "14px", lineHeight: 1.4 }}
                              >
                                {voucher.voucher_title}
                              </Text>
                            }
                            description={
                              <div style={{ marginTop: 4 }}>
                                {/* First row: Code and Type */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    marginBottom: 4,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: "12px", minWidth: 40 }}
                                  >
                                    Code:
                                  </Text>
                                  <Text
                                    code
                                    style={{
                                      // fontSize: "11px",
                                      // backgroundColor: "#f5f5f5",
                                      // padding: "2px 8px",
                                      borderRadius: 4,
                                      fontFamily: "monospace",
                                      // maxWidth: "140px",
                                      overflow: "hidden",
                                    }}
                                    title={voucher.voucher_code}
                                  >
                                    {voucher.voucher_code.length > 16
                                      ? `${voucher.voucher_code.substring(
                                          0,
                                          8
                                        )}...${voucher.voucher_code.substring(
                                          voucher.voucher_code.length - 8
                                        )}`
                                      : voucher.voucher_code}
                                  </Text>
                                  <Tag
                                    color="blue"
                                    style={{
                                      margin: 0,
                                      fontSize: "11px",
                                      padding: "0 6px",
                                      lineHeight: "18px",
                                    }}
                                  >
                                    {voucher.price_type}
                                  </Tag>
                                </div>

                                {/* Second row: Restaurant and Expiry */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: "12px", minWidth: 40 }}
                                  >
                                    Store:
                                  </Text>
                                  <Text
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {voucher.store_name}
                                  </Text>
                                </div>

                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    marginTop: 2,
                                  }}
                                >
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: "12px", minWidth: 40 }}
                                  >
                                    Expires:
                                  </Text>
                                  <Text style={{ fontSize: "12px" }}>
                                    {moment(voucher.expired_date).format(
                                      "DD MMM YYYY"
                                    )}
                                  </Text>
                                </div>
                              </div>
                            }
                          />
                        </List.Item>
                      )}
                      style={{ marginTop: 12 }}
                    />
                  </>
                )}
              </Card>
            </Col>

            {/* Right Column - Table Search */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <Space>
                    <SearchOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                    Search Table
                  </Space>
                }
                extra={
                  !isEditMode && (
                    <Tag color={isEditMode ? "orange" : "green"}>
                      {isEditMode ? "Save Vouchers First" : "Ready to Search"}
                    </Tag>
                  )
                }
              >
                {isEditMode ? (
                  // EDIT MODE - Table search disabled
                  <div>
                    <Alert
                      message="Save Your Vouchers First"
                      description="Please save your voucher before search table"
                      type="info"
                      showIcon
                    />
                  </div>
                ) : (
                  // SAVED MODE - Table search enabled
                  <>
                    <Space
                      direction="vertical"
                      style={{ width: "100%" }}
                      size="large"
                    >
                      {/* Consolidated Restaurant & Party Info */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "16px",
                          backgroundColor: "#fafafa",
                          borderRadius: "8px",
                          border: "1px solid #f0f0f0",
                        }}
                      >
                        <div>
                          <Text strong style={{ fontSize: "16px" }}>
                            {selectedRestaurant?.name ||
                              "No restaurant selected"}
                          </Text>
                          <div style={{ marginTop: 4 }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              {savedVouchers.length} vouchers selected
                            </Text>
                          </div>
                        </div>

                        <div style={{ textAlign: "center" }}>
                          <Text
                            strong
                            style={{ fontSize: "20px", color: "#e90056" }}
                          >
                            {adultPax}
                          </Text>
                          <div>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              Adults
                            </Text>
                          </div>
                        </div>

                        {childPax > 0 && (
                          <div style={{ textAlign: "center" }}>
                            <Text
                              strong
                              style={{ fontSize: "20px", color: "#52c41a" }}
                            >
                              {childPax}
                            </Text>
                            <div>
                              <Text
                                type="secondary"
                                style={{ fontSize: "12px" }}
                              >
                                Children
                              </Text>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Inline Date Selection & Search */}
                      <div>
                        <Text
                          strong
                          style={{ marginBottom: 8, display: "block" }}
                        >
                          Booking Date
                        </Text>
                        <div
                          style={{
                            display: "flex",
                            gap: "12px",
                            alignItems: "flex-start",
                          }}
                        >
                          {/* Date Picker - 70% width */}
                          <DatePicker
                            value={bookingDate ? moment(bookingDate) : null}
                            onChange={(date, dateString) =>
                              setBookingDate(dateString)
                            }
                            disabledDate={(current) => {
                              const available_days =
                                savedVouchers[0]?.available_days;

                              // Disable past dates
                              if (moment().add(-1, "days") >= current) {
                                return true;
                              }

                              // If no available_days data, allow all future dates
                              if (
                                !available_days ||
                                available_days.length === 0
                              ) {
                                return false;
                              }

                              // Get the day name in lowercase (e.g., "monday", "tuesday")
                              const dayName = current
                                .format("dddd")
                                .toLowerCase();

                              // Disable if the day is NOT in available_days array
                              return !available_days.includes(dayName);
                            }}
                            style={{
                              flex: 7, // 70% width
                              marginTop: 0,
                            }}
                            size="large"
                            placeholder="Select booking date"
                          />

                          {/* Search Button - 30% width */}
                          <Button
                            type="primary"
                            icon={
                              <SearchOutlined
                                style={{ color: "#fff" }}
                                onPointerEnterCapture={undefined}
                                onPointerLeaveCapture={undefined}
                              />
                            }
                            loading={searchingTables}
                            onClick={handleSearchTables}
                            disabled={
                              !bookingDate || savedVouchers.length === 0
                            }
                            size="large"
                            style={{
                              flex: 1, // 30% width
                              backgroundColor: "#e90056",
                              borderColor: "#e90056",
                              height: "40px",
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                            }}
                          ></Button>
                        </div>

                        {/* Available days hint */}
                        {savedVouchers.length > 0 &&
                          savedVouchers[0]?.available_days && (
                            <div style={{ marginTop: 8 }}>
                              <Text
                                type="secondary"
                                style={{ fontSize: "12px" }}
                              >
                                Available on:{" "}
                                {savedVouchers[0].available_days.join(", ")}
                              </Text>
                            </div>
                          )}
                      </div>
                    </Space>

                    <Divider />
                    {/* Default Empty State - Show when no search has been performed */}
                    {!hasSearched && (
                      <div
                        // title="Ready to Search"
                        style={{
                          marginTop: 24,
                          textAlign: "center",
                          padding: "40px",
                        }}
                        // bodyStyle={{ textAlign: "center", padding: "40px" }}
                      >
                        <div style={{ marginBottom: 16 }}>
                          <SearchOutlined
                            style={{ fontSize: 48, color: "#d9d9d9" }}
                            onPointerEnterCapture={undefined}
                            onPointerLeaveCapture={undefined}
                          />
                        </div>
                        <Text type="secondary">
                          Select date to find available time slots
                        </Text>
                        {/* <div style={{ marginTop: 8 }}>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            {savedVouchers.length} vouchers selected •{" "}
                            {adultPax} Adults
                            {childPax > 0 ? `, ${childPax} Children` : ""}
                          </Text>
                        </div> */}
                      </div>
                    )}

                    {/* Table Search Results */}
                    {hasSearched && (
                      <div style={{ marginTop: 24 }}>
                        {searchingTables ? (
                          <div style={{ textAlign: "center", padding: 20 }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 12 }}>
                              <Text>Searching for available tables...</Text>
                            </div>
                          </div>
                        ) : table.timeSlots && table.timeSlots.length > 0 ? (
                          <>
                            <Text strong style={{ fontSize: 16 }}>
                              Available Time Slots ({table.timeSlots.length})
                            </Text>
                            <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
                              {table.timeSlots.map(
                                (slot: any, index: number) => (
                                  <Col key={index} xs={12} sm={8}>
                                    <Card
                                      size="small"
                                      onClick={() => handleSelectTable(slot)}
                                      style={{
                                        cursor: "pointer",
                                        border: "1px solid #d9d9d9",
                                        borderRadius: "8px",
                                        textAlign: "center",
                                      }}
                                      bodyStyle={{ padding: "12px 8px" }}
                                      hoverable
                                    >
                                      <div style={{ marginBottom: 8 }}>
                                        <Text
                                          strong
                                          style={{ fontSize: "16px" }}
                                        >
                                          {slot.time_slot}
                                        </Text>
                                      </div>
                                      <div style={{ marginBottom: 4 }}>
                                        <Text
                                          type="secondary"
                                          style={{ fontSize: "12px" }}
                                        >
                                          Tables: {slot.Table_Left}
                                        </Text>
                                      </div>
                                    </Card>
                                  </Col>
                                )
                              )}
                            </Row>
                          </>
                        ) : (
                          <Alert
                            message="No Tables Available"
                            description="No available time slots found for the selected date. Please try a different date."
                            type="warning"
                            showIcon
                            action={
                              <Button
                                size="small"
                                onClick={handleSearchTables}
                                style={{
                                  backgroundColor: "#e90056",
                                  borderColor: "#e90056",
                                  color: "white",
                                }}
                              >
                                Search Again
                              </Button>
                            }
                          />
                        )}
                      </div>
                    )}
                  </>
                )}
              </Card>
            </Col>
          </Row>
        </div>

        <Footer />
      </div>
    </div>
  );
});

export default VoucherTableGuestBooking;
